import { iconNavBusca, iconNavFechar } from '@bsds-lib-component/icons/icons';
import { BsdsCard, BsdsCheckbox, BsdsInput } from '@bsds-lib-component/react';
import React, { useId, useMemo, useState } from 'react';
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';

import { AdhesionProposalTable, Dropdown, SystemMessage, useSystemContainer, VidaTableListToolbar, Visibility } from '../../../../components';
import AdhesionProposalDetailModal from '../../../../components/pagesComponents/vidaPageComponents/modal/AdhesionProposalDetailModal';
import Button from '../../../../components/button/Button';
import { useVidaAdhesionProposal, useVidaTableListControls } from '../../../../hooks';
import { useSystemMessage } from '../../../../hooks/useSystemMessage';
import { useCompanyStore } from '../../../../store/companyStore';
import { extractBsdsInputValue } from '../../../../utils/bsdsInputUtils';
import { formatCpf, cleanCpf } from '../../../../utils/cpfUtils';
import {
  getProponentStatusColor,
  mapProponentStatus,
} from '../../../../utils/proponentStatusUtils';
import {
  ADHESION_PROPOSAL_SORT_OPTIONS,
  EMPTY_ADHESION_PROPOSAL_PANEL_FILTERS,
  filterAdhesionProposalTableBySearch,
  filterAdhesionProposalTableRows,
  sortAdhesionProposalTableRows,
} from '../../../../utils/adhesionProposalUtil';
import { subestipulanteDropdownOptions } from '../../../../utils/subestipulanteUtil';
import type { ValidacaoPAProponenteItemResponse, ProponenteSubestipulanteItemResponse } from '../../../../types/vida/adhesionProposal/adhesionProposalType';
import type { AdhesionProposalItem } from '../../../../types/vida/table/adhesionProposalTableType';

const formatDateToYyyyMMdd = (date: string): string => date.replace(/-/g, '');

const SUBESTIPULANTE_TODOS_VALUE = '0';
const SUBESTIPULANTE_TODOS_OPTION = { label: 'Todos', value: SUBESTIPULANTE_TODOS_VALUE };

type SearchType = 'data' | 'proponent';

/**
 * Maps ValidacaoPAProponenteItemResponse to AdhesionProposalItem format
 * for compatibility with AdhesionProposalTable component
 */
const mapToAdhesionProposalItem = (
  item: ValidacaoPAProponenteItemResponse
): AdhesionProposalItem => {
  const proposerCpf = `${item.cpf}${item.codigoCpf}`;

  return {
    id: item.identProponente,
    contractProductId: item.identSubestipulante,
    contractNumber: item.apolice,
    status: item.status.toString(),
    statusAt: item.dataRecepcao || '',
    prazoDec: item.prazoDecorrido ?? null,
    proposerCpf,
    proposerName: item.nomeProponente,
    importSource: item.descrTipoImport,
    subCompanyId: item.identSubestipulante,
    subCompanyCod: item.codigoSubestipulante,
    subCompanyName: item.subEstipulante,
    companyId: item.codigoSucursal,
    companyName: item.empresa,
    validityStart: '',
    validityEnd: '',
    numVersaoPa: item.numVesaoPa,
  };
};

const mapProponenteSubestipulanteToAdhesionProposalItem = (
  item: ProponenteSubestipulanteItemResponse,
  policyNumber: string
): AdhesionProposalItem => ({
  id: item.identProponente,
  contractProductId: item.identSubestipulante,
  contractNumber: policyNumber,
  status: item.statusProponente.toString(),
  statusAt: '',
  prazoDec: null,
  proposerCpf: item.cpf,
  proposerName: item.nome || item.nmProponenteSocial,
  importSource: '',
  subCompanyId: item.identSubestipulante,
  subCompanyName: '',
  companyId: 0,
  companyName: '',
  validityStart: '',
  validityEnd: '',
});

// Helper function to format date time from ISO to dd/MM/yyyy HH:mm
const formatDateTime = (dateTimeString: string): string => {
  if (!dateTimeString) return '-';
  try {
    const date = new Date(dateTimeString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  } catch {
    return dateTimeString;
  }
};

const AdhesionProposalTab: React.FC = () => {
  const selectedCompany = useCompanyStore((s) => s.selectedCompany);
  const policyNumeroApolice = selectedCompany?.policySelection?.numeroApolice?.trim() ?? '';

  const {
    isLoading,
    clearError,
    summary,
    proposals,
    proponentProposals,
    currentPage,
    totalPages,
    totalElements,
    loadAdhesionProposalData,
    loadAdhesionProposalByProponentData,
  } = useVidaAdhesionProposal();
  const { showMessage, message, type, show } = useSystemMessage();
  const { setLoading } = useSystemContainer();
  const mainContentId = useId();
  const searchFormId = useId();
  const resultsId = useId();
  const emptyStateId = useId();
  const messageId = useId();
  const subestipulanteSelectId = useId();

  const [dtInicio, setDtInicio] = useState('');
  const [dtFim, setDtFim] = useState('');
  const [codSubestipulante, setCodSubestipulante] = useState(SUBESTIPULANTE_TODOS_VALUE);
  const [searchType, setSearchType] = useState<SearchType>('data');
  const [identSubestipulante, setIdentSubestipulante] = useState('');
  const [nmProponente, setNmProponente] = useState('');
  const [cpfProponente, setCpfProponente] = useState('');
  const [cdMatricula, setCdMatricula] = useState('');

  const subestipulanteOptionsByDate = useMemo(
    () => [
      SUBESTIPULANTE_TODOS_OPTION,
      ...subestipulanteDropdownOptions(selectedCompany?.subestipulantes, false, 'codSubestipulanteOut'),
    ],
    [selectedCompany?.subestipulantes]
  );

  const subestipulanteOptionsByProponent = useMemo(
    () => subestipulanteDropdownOptions(selectedCompany?.subestipulantes, false, 'identSubestipulanteOut'),
    [selectedCompany?.subestipulantes]
  );

  const subestipulanteOptions = searchType === 'data'
    ? subestipulanteOptionsByDate
    : subestipulanteOptionsByProponent;

  const [hasSearchResults, setHasSearchResults] = useState<boolean>(false);
  const [hasSearched, setHasSearched] = useState<boolean>(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<AdhesionProposalItem | null>(null);

  const getApoliceForSearch = () => policyNumeroApolice;

  React.useEffect(() => {
    if (searchType === 'data') {
      const allowed = new Set(subestipulanteOptionsByDate.map((option) => option.value));
      if (codSubestipulante && !allowed.has(codSubestipulante)) {
        setCodSubestipulante(SUBESTIPULANTE_TODOS_VALUE);
      }
      return;
    }

    const allowed = new Set(subestipulanteOptionsByProponent.map((option) => option.value));
    if (identSubestipulante && !allowed.has(identSubestipulante)) {
      setIdentSubestipulante('');
    }
  }, [searchType, subestipulanteOptionsByDate, subestipulanteOptionsByProponent, codSubestipulante, identSubestipulante]);

  const pieChartData = summary?.map((item) => ({
    name: mapProponentStatus(item.status),
    value: item.count,
    percentage: item.percentage,
    status: item.status,
  })) || [];

  const mappedProposals = useMemo(() => {
    if (searchType === 'proponent') {
      return proponentProposals.map((item) => {
        const mapped = mapProponenteSubestipulanteToAdhesionProposalItem(item, policyNumeroApolice);
        const subestipulante = selectedCompany?.subestipulantes?.find(
          (sub) => sub.identSubestipulanteOut === item.identSubestipulante,
        );

        if (!subestipulante) {
          return mapped;
        }

        return {
          ...mapped,
          subCompanyCod: subestipulante.codSubestipulanteOut,
          subCompanyName: subestipulante.nomeSubestipulanteOut?.trim() || mapped.subCompanyName,
        };
      });
    }
    return proposals.map(mapToAdhesionProposalItem);
  }, [proposals, proponentProposals, searchType, policyNumeroApolice, selectedCompany?.subestipulantes]);

  const listControls = useVidaTableListControls({
    emptyFilters: EMPTY_ADHESION_PROPOSAL_PANEL_FILTERS,
    loadedRowCount: mappedProposals.length,
    debounceMs: 400,
  });

  const {
    debouncedSearch,
    sortBy,
    appliedPanelFilters,
    panelFilters,
    updatePanelFilter,
    resetAll: resetTableFilters,
    isPanelFilterActive,
  } = listControls;

  const filteredProposals = useMemo(() => {
    let rows = mappedProposals;
    rows = filterAdhesionProposalTableBySearch(rows, debouncedSearch, formatCpf, formatDateTime);
    rows = filterAdhesionProposalTableRows(rows, appliedPanelFilters);
    return rows;
  }, [mappedProposals, debouncedSearch, appliedPanelFilters]);

  const sortedProposals = useMemo(
    () => sortAdhesionProposalTableRows(filteredProposals, sortBy),
    [filteredProposals, sortBy],
  );

  const isTableFilterActive = debouncedSearch.trim() !== '' || isPanelFilterActive;

  const executeDateSearch = async (page: number, size: number) => {
    const apolice = getApoliceForSearch();
    const dtInicioApi = formatDateToYyyyMMdd(dtInicio);
    const dtFimApi = formatDateToYyyyMMdd(dtFim);
    const identSub = codSubestipulante.trim() === '' ? undefined : codSubestipulante.trim();

    await loadAdhesionProposalData(
      apolice,
      dtInicioApi,
      dtFimApi,
      identSub,
      page,
      size
    );
  };

  const executeProponentSearch = async (page: number, size: number) => {
    const cpfDigits = cleanCpf(cpfProponente);
    const cdCpf = cpfDigits.length >= 9 ? cpfDigits.slice(0, 9) : undefined;
    const cdCtrlCpf = cpfDigits.length >= 11 ? cpfDigits.slice(9, 11) : undefined;
    const matricula = cdMatricula.trim() || undefined;
    const nome = nmProponente.trim() || undefined;

    await loadAdhesionProposalByProponentData(
      Number(identSubestipulante),
      nome,
      cdCpf,
      cdCtrlCpf,
      matricula,
      page,
      size
    );
  };

  const handleSearchTypeChange = (newType: SearchType) => {
    if (newType === searchType) {
      return;
    }
    setSearchType(newType);
    setHasSearchResults(false);
    setHasSearched(false);
    resetTableFilters();
    clearError();
  };

  const handleSearch = async () => {
    if (searchType === 'data') {
      if (!getApoliceForSearch()) {
        clearError();
        showMessage('Informe o número da apólice para pesquisar', 'warning');
        return;
      }

      if (!dtInicio || !dtFim) {
        clearError();
        showMessage('Informe a data inicial e a data final para pesquisar', 'warning');
        return;
      }
    } else if (!identSubestipulante.trim()) {
      clearError();
      showMessage('Selecione o Sub-Estipulante para pesquisar', 'warning');
      return;
    }

    clearError();
    setHasSearchResults(false);
    setHasSearched(false);
    resetTableFilters();

    try {
      if (searchType === 'data') {
        await executeDateSearch(0, 6);
      } else {
        await executeProponentSearch(0, 6);
      }
      setHasSearched(true);
    } catch (err) {
      console.error('Erro ao carregar propostas de adesão:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar propostas de adesão';
      showMessage(errorMsg, 'error');
      setHasSearchResults(false);
      setHasSearched(true);
    }
  };

  const uiCurrentPage = currentPage + 1;

  const handlePageChange = async (page: number) => {
    const backendPage = page - 1;

    if (searchType === 'data') {
      if (!getApoliceForSearch() || !dtInicio || !dtFim) {
        return;
      }

      try {
        await executeDateSearch(backendPage, 6);
      } catch (err) {
        console.error('Erro ao carregar página:', err);
        const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar página';
        showMessage(errorMsg, 'error');
      }
      return;
    }

    if (!identSubestipulante.trim()) {
      return;
    }

    try {
      await executeProponentSearch(backendPage, 6);
    } catch (err) {
      console.error('Erro ao carregar página:', err);
      const errorMsg = err instanceof Error ? err.message : 'Erro ao carregar página';
      showMessage(errorMsg, 'error');
    }
  };

  const handleClearFilters = () => {
    setDtInicio('');
    setDtFim('');
    setCodSubestipulante(SUBESTIPULANTE_TODOS_VALUE);
    setIdentSubestipulante('');
    setNmProponente('');
    setCpfProponente('');
    setCdMatricula('');
    setHasSearchResults(false);
    setHasSearched(false);
    resetTableFilters();
    clearError();
  };

  const handleProposalRowClick = (proposal: AdhesionProposalItem) => {
    setSelectedProposal(proposal);
    setIsDetailModalOpen(true);
  };

  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedProposal(null);
  };

  const handleInputChange = (setter: (value: string) => void) => (
    event: React.FormEvent<HTMLBsdsInputElement>
  ) => {
    const target = event.target as HTMLInputElement;
    setter(target?.value || '');
  };

  const handleCpfInputChange = (event: React.FormEvent<HTMLBsdsInputElement>) => {
    const target = event.target as HTMLInputElement;
    setCpfProponente(formatCpf(target?.value || ''));
  };

  const isSearchEnabled = searchType === 'data'
    ? Boolean(getApoliceForSearch()) && Boolean(dtInicio) && Boolean(dtFim)
    : Boolean(identSubestipulante.trim());

  const isSearchDisabled = isLoading || !isSearchEnabled;

  const searchButtonAriaLabel = isLoading
    ? 'Pesquisando propostas de adesão...'
    : searchType === 'data'
      ? !getApoliceForSearch()
        ? 'Preencha o número da apólice para pesquisar'
        : !dtInicio || !dtFim
          ? 'Preencha as datas para pesquisar'
          : 'Pesquisar propostas de adesão por data'
      : !identSubestipulante.trim()
        ? 'Selecione o Sub-Estipulante para pesquisar'
        : 'Pesquisar propostas de adesão por dados do proponente';

  React.useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  React.useEffect(() => {
    if (hasSearched && !isLoading) {
      setHasSearchResults(true);
    }
  }, [hasSearched, isLoading]);

  return (
    <div
      className="bsds-width-100 bsds-mt-12 bsds-p-8"
      role="main"
      aria-busy={isLoading}
      aria-label="Consulta de propostas de adesão"
      id={mainContentId}
    >
      {show && (
        <div
          id={messageId}
          className="bsds-mb-16"
          role={type === 'error' ? 'alert' : 'status'}
          aria-live={type === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <SystemMessage
            message={message}
            type={type}
            show={show}
          />
        </div>
      )}
      <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center">
        <div className="bsds-display-flex bsds-align-items-center">
          <div className="w-[2.5px] h-8  bg-red-600 bsds-mr-8" aria-hidden="true" />
        </div>
        <h5 className="bsds-ml-8 font-normal">Acompanhamento de Propostas de Adesão</h5>
      </div>

      <div
        id={searchFormId}
        className="bsds-mb-24 bsds-mt-24"
        role="region"
        aria-label="Formulário de busca de propostas de adesão"
      >
        <div className="bsds-mb-16">
          <div className="bsds-mb-16" role="radiogroup" aria-label="Tipo de busca">
            <div className="bsds-display-flex flex-col md:flex-row bsds-flex-wrap gap-2 md:gap-16">
              <div className="bsds-display-flex bsds-align-items-center bsds-gap-8 bsds-pr-48">
                <BsdsCheckbox
                  checked={searchType === 'data'}
                  onBsdsChange={() => handleSearchTypeChange('data')}
                  color="primary"
                  aria-label="Buscar por Data"
                />
                <span
                  className="bsds-text-regular bsds-pl-12"
                >
                  Data
                </span>
              </div>
              <div className="bsds-display-flex bsds-align-items-center bsds-gap-8">
                <BsdsCheckbox
                  checked={searchType === 'proponent'}
                  onBsdsChange={() => handleSearchTypeChange('proponent')}
                  color="primary"
                  aria-label="Buscar por Dados do Proponente"
                />
                <span
                  className="bsds-text-regular bsds-pl-12"
                >
                  Proponente
                </span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bsds-mb-16">
            <BsdsInput
              value={policyNumeroApolice}
              label={searchType === 'data' ? 'Número da Apólice *' : 'Número da Apólice'}
              type="text"
              color="primary"
              platform="web"
              className="bsds-width-100"
              readonly
              aria-label="Número da apólice selecionada"
              aria-readonly="true"
            />

            {searchType === 'data' ? (
              <>
                <BsdsInput
                  value={dtInicio}
                  onInput={handleInputChange(setDtInicio)}
                  label="Data Inicial *"
                  placeholder="yyyy-MM-dd"
                  type="date"
                  color="primary"
                  platform="web"
                  className="bsds-width-100"
                  aria-label="Data inicial (obrigatório)"
                  aria-required
                />

                <BsdsInput
                  value={dtFim}
                  onInput={handleInputChange(setDtFim)}
                  label="Data Final *"
                  placeholder="yyyy-MM-dd"
                  type="date"
                  color="primary"
                  platform="web"
                  className="bsds-width-100"
                  aria-label="Data final (obrigatória)"
                  aria-required
                />

                <div className="md:col-span-2">
                  <Dropdown
                    id={subestipulanteSelectId}
                    label="Sub-Estipulante"
                    placeholder="Selecione um Sub-Estipulante"
                    value={codSubestipulante}
                    onChange={setCodSubestipulante}
                    options={subestipulanteOptions}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="md:col-span-2">
                  <Dropdown
                    id={subestipulanteSelectId}
                    label="Sub-Estipulante *"
                    placeholder="Selecione um Sub-Estipulante"
                    value={identSubestipulante}
                    onChange={setIdentSubestipulante}
                    options={subestipulanteOptions}
                  />
                </div>

                <BsdsInput
                  value={nmProponente}
                  onInput={handleInputChange(setNmProponente)}
                  label="Nome do Proponente"
                  placeholder="Nome do Proponente"
                  type="text"
                  clearInput={false}
                  color="primary"
                  platform="web"
                  className="bsds-width-100"
                  aria-label="Nome do proponente"
                />

                <BsdsInput
                  value={cpfProponente}
                  onInput={handleCpfInputChange}
                  label="CPF"
                  placeholder="000.000.000-00"
                  type="text"
                  color="primary"
                  clearInput={false}
                  platform="web"
                  className="bsds-width-100"
                  aria-label="CPF do proponente"
                />

                <BsdsInput
                  value={cdMatricula}
                  onInput={handleInputChange(setCdMatricula)}
                  label="Matrícula"
                  placeholder="Matrícula"
                  type="text"
                  clearInput={false}
                  color="primary"
                  platform="web"
                  className="bsds-width-100"
                  aria-label="Matrícula do proponente"
                />
              </>
            )}
          </div>

          <div
            className="flex flex-col-reverse sm:flex-row sm:justify-end gap-4"
            role="group"
            aria-label="Ações do formulário de busca"
          >
            <Button
              onClick={handleClearFilters}
              color="secondary"
              fill="outline"
              icon={iconNavFechar}
              iconPosition="end"
              aria-label="Limpar todos os filtros do formulário"
            >
              Limpar Filtros
            </Button>

            <Button
              onClick={handleSearch}
              color="primary"
              fill="solid"
              loading={isLoading}
              disabled={isSearchDisabled}
              icon={iconNavBusca}
              iconPosition="start"
              aria-label={searchButtonAriaLabel}
            >
              Pesquisar
            </Button>
          </div>
        </div>
      </div>

      <Visibility isActive={hasSearched && hasSearchResults} animated animationDuration={300}>
        <div
          id={resultsId}
          className="bsds-mb-16 bsds-mt-34"
          role="region"
          aria-label="Resultados da busca de propostas de adesão"
        >
          <BsdsCard className="bsds-p-16" role="region" aria-label="Gráfico de status das propostas">
            <div className="flex items-center mb-4">
              <div className="w-[2.5px] h-6 bg-red-600 mr-3" aria-hidden="true" />
              <h4 className="opacity-80">Distribuição por Status</h4>
            </div>
            {pieChartData.length > 0 ? (
              <div className="flex flex-col md:flex-row gap-8">
                <div className="w-full md:w-1/2 h-[400px] min-h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={150}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieChartData.map((item, index) => (
                          <Cell key={`cell-${index}`} fill={getProponentStatusColor(item.status)} />
                        ))}
                      </Pie>
                      <Tooltip
                        separator=": "
                        formatter={(value, _name, item) => {
                          const data = item?.payload as { percentage?: number } | undefined;
                          const percentage = data?.percentage ?? 0;
                          const numericValue = typeof value === 'number' ? value : Number(value ?? 0);
                          return [`${numericValue} (${percentage.toFixed(1)}%)`, 'Quantidade'];
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                <div className="w-full md:w-1/2 flex flex-col gap-[20px] rounded-md">
                  {pieChartData.map((item, index) => {
                    const color = getProponentStatusColor(item.status);
                    return (
                      <div
                        key={`status-card-${index}`}
                        className="h-[60px] px-3 bg-gray-50 rounded-md flex items-center gap-3 max-w-full"
                        role="listitem"
                        aria-label={`${item.name}: ${item.value} propostas (${item.percentage.toFixed(1)}%)`}
                      >
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5"
                          style={{ backgroundColor: color }}
                          aria-hidden="true"
                        />
                        <div className="flex flex-1 min-w-0 justify-between items-center h-full">
                          <span className="font-semibold text-sm truncate pt-0.5">
                            {item.name}
                          </span>
                          <div className="flex flex-col items-end">
                            <span className="font-semibold text-sm">
                              {item.value}
                            </span>
                            <span className="text-xs text-gray-600">
                              {item.percentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p className="bsds-text-regular text-gray-600">Nenhum dado disponível para o gráfico</p>
            )}
          </BsdsCard>

          <div
            className="bsds-mt-16"
            role="region"
            aria-label="Informações das propostas de adesão"
          >

            <VidaTableListToolbar
              groupAriaLabel="Busca na lista, ordenação e filtros de propostas"
              searchPlaceholder="Filtre por ID, CPF, nome, status ou Sub-Estipulante"
              searchAriaLabel="Filtre por ID, CPF, nome, status ou Sub-Estipulante"
              sortId="sort-adhesion-proposal"
              sortOptions={ADHESION_PROPOSAL_SORT_OPTIONS}
              filtersPanelAriaLabel="Configurações de filtros de propostas"
              entityLabel="propostas"
              controls={listControls}
              className="bsds-mb-16 bsds-mt-16"
              showSearchClearInput={false}
            >
              <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="ID"
                    value={panelFilters.id}
                    onInput={(e) => updatePanelFilter('id', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Status"
                    value={panelFilters.status}
                    onInput={(e) => updatePanelFilter('status', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
              </div>
              <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="CPF"
                    value={panelFilters.cpf}
                    onInput={(e) => updatePanelFilter('cpf', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Proponente"
                    value={panelFilters.proponente}
                    onInput={(e) => updatePanelFilter('proponente', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
              </div>
              <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Sub-Estipulante"
                    value={panelFilters.subEstipulante}
                    onInput={(e) => updatePanelFilter('subEstipulante', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
              </div>
            </VidaTableListToolbar>

            <BsdsCard className="bsds-p-16">
              <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                <h6 className="opacity-80">Propostas de Adesão</h6>
              </div>
              <AdhesionProposalTable
                proposals={sortedProposals}
                formatDate={formatDateTime}
                formatCpf={formatCpf}
                getStatusColor={getProponentStatusColor}
                emptyMessage={
                  isTableFilterActive
                    ? 'Nenhuma proposta encontrada para os filtros informados'
                    : 'Nenhuma proposta encontrada'
                }
                onRowClick={handleProposalRowClick}
                onExport={(selectedProposals) => {
                  console.log('Exportar propostas:', selectedProposals);
                }}
                currentPage={uiCurrentPage}
                totalPages={totalPages}
                totalItems={!isLoading && !isTableFilterActive ? totalElements : 0}
                onPageChange={handlePageChange}
              />
            </BsdsCard>
          </div>
        </div>
      </Visibility>

      <Visibility isActive={hasSearched && !hasSearchResults} animated animationDuration={300}>
        <div
          id={emptyStateId}
          className="bsds-display-flex bsds-justify-content-center bsds-align-items-center bsds-mt-64 bsds-mb-64"
          role="status"
          aria-live="polite"
          aria-label="Resultado da busca"
        >
          <div className="bsds-text-center">
            <p className="bsds-text-regular bsds-text-lg text-gray-600">
              Dados não encontrados para a pesquisa realizada
            </p>
          </div>
        </div>
      </Visibility>

      <AdhesionProposalDetailModal
        isOpen={isDetailModalOpen}
        proposal={selectedProposal}
        formatCpf={formatCpf}
        onClose={handleCloseDetailModal}
      />
    </div>
  );
};

export default AdhesionProposalTab;
